import { CampoValor, DadosComentario, DadosComentariosMultiplos } from "./types";
import type { Email } from "../DemandaCriada/types";
import { parseJsonSafely } from "../../../../../../../utility/jsonUtils";
import { mapearEmail } from "../../../../../../../utility/historicoLogUtils";

export const processarDadosComentario = (logs: any[]): DadosComentario | DadosComentariosMultiplos => {
    const logsComentarios = logs.filter((log: any) => log.entidade_nome === 'comentario');
    
    if (logsComentarios.length === 0) {
        return {
            campos: [],
            email: null,
            idComentario: undefined,
            idProposta: undefined,
            idComentarioPai: undefined
        };
    }

    // Se houver múltiplos logs de comentários, processar todos (caso de sequência de exclusões)
    if (logsComentarios.length > 1) {
        const comentariosProcessados = logsComentarios.map(logComentario => processarLogComentario(logComentario, logs));
        const email = mapearEmail(logs);
        return {
            comentarios: comentariosProcessados,
            email
        };
    }

    // Caso único comentário
    const logComentario = logsComentarios[0];
    return processarLogComentario(logComentario, logs);
}

const processarLogComentario = (logComentario: any, todosLogs: any[]): DadosComentario => {

    let dadosDepois = parseJsonSafely(logComentario.dados_depois);
    const dadosAntes = parseJsonSafely(logComentario.dados_antes);
    const dadosExtra = parseJsonSafely(logComentario.dados_extra);
    
    // Se dados_depois é null, undefined, objeto vazio {}, string "null" ou string vazia, significa que foi excluído
    // Garantir que null seja tratado corretamente
    if (dadosDepois === null || dadosDepois === 'null' || dadosDepois === undefined) {
        dadosDepois = null;
    }
    
    // Verificar se é objeto vazio (exclusão)
    const isObjetoVazio = typeof dadosDepois === 'object' && dadosDepois !== null && Object.keys(dadosDepois).length === 0;
    const dadosDepoisVazio = dadosDepois === null 
        || dadosDepois === undefined
        || isObjetoVazio;
    const isExcluido = dadosDepoisVazio && dadosAntes;
    const dadosParaProcessar = isExcluido ? dadosAntes : dadosDepois;
    
    const campos: CampoValor[] = [];
    
    // Para exclusões, mostrar "Antes" e "Depois" (Depois vazio)
    if (isExcluido) {
        // Texto: Antes com texto, Depois vazio
        if (dadosAntes?.texto) {
            campos.push({
                campo: 'Texto (Antes)',
                valor: dadosAntes.texto
            });
            campos.push({
                campo: 'Texto (Depois)',
                valor: '—'
            });
        }
        
        // Para: Antes com nome e tipo, Depois vazio
        if (dadosAntes?.tipo_destinatario) {
            let tipoDestinatario: string;
            let nomeDestinatario: string | null;
            
            if (dadosAntes.tipo_destinatario === 'OperadoresInterno') {
                nomeDestinatario = 'Todos os Operadores (comunicação interna)';
                tipoDestinatario = 'Operador';
            } else {
                tipoDestinatario = dadosAntes.tipo_destinatario === 'Solicitante' 
                    ? 'Solicitante' 
                    : dadosAntes.tipo_destinatario === 'Profissional'
                    ? 'Profissional'
                    : 'Operador';
                
                nomeDestinatario = dadosAntes?.destinatario_nome 
                    || dadosExtra?.nome_destinatario 
                    || dadosAntes?.nome_destinatario 
                    || null;
            }
            
            const valorAntes = nomeDestinatario 
                ? `${nomeDestinatario} (${tipoDestinatario})`
                : `(${tipoDestinatario})`;
            
            campos.push({
                campo: 'Para (Antes)',
                valor: valorAntes
            });
            campos.push({
                campo: 'Para (Depois)',
                valor: '—'
            });
        }
    } else {
        // Para criação/resposta, mostrar campos normais
        if (dadosParaProcessar?.texto) {
            campos.push({
                campo: 'Texto',
                valor: dadosParaProcessar.texto
            });
        }
        
        if (dadosParaProcessar?.tipo_destinatario) {
            let tipoDestinatario: string;
            let nomeDestinatario: string | null;
            
            if (dadosParaProcessar.tipo_destinatario === 'OperadoresInterno') {
                nomeDestinatario = 'Todos os Operadores (comunicação interna)';
                tipoDestinatario = 'Operador';
            } else {
                tipoDestinatario = dadosParaProcessar.tipo_destinatario === 'Solicitante' 
                    ? 'Solicitante' 
                    : dadosParaProcessar.tipo_destinatario === 'Profissional'
                    ? 'Profissional'
                    : 'Operador';
                
                nomeDestinatario = dadosExtra?.nome_destinatario 
                    || dadosParaProcessar?.nome_destinatario 
                    || dadosParaProcessar?.destinatario_nome 
                    || null;
            }
            
            if (nomeDestinatario) {
                campos.push({
                    campo: 'Para',
                    valor: `${nomeDestinatario} (${tipoDestinatario})`
                });
            } else {
                campos.push({
                    campo: 'Para',
                    valor: `(${tipoDestinatario})`
                });
            }
        }
        
        // Para edições, mostrar antes e depois
        if (dadosAntes?.texto && dadosDepois?.texto && dadosAntes.texto !== dadosDepois.texto) {
            campos.push({
                campo: 'Texto (Antes)',
                valor: dadosAntes.texto
            });
            campos.push({
                campo: 'Texto (Depois)',
                valor: dadosDepois.texto
            });
        }
    }
    
    if (dadosExtra?.id_comentario_pai) {
        campos.push({
            campo: 'Comentário Pai',
            valor: `#${String(dadosExtra.id_comentario_pai).padStart(4, '0')}`
        });
    }
    
    if (dadosExtra?.quantidade_removida) {
        campos.push({
            campo: 'Quantidade Removida',
            valor: String(dadosExtra.quantidade_removida)
        });
    }

    const email = mapearEmail(todosLogs);
    const idComentario = logComentario.entidade_id;
    const idProposta = dadosParaProcessar?.id_proposta || dadosAntes?.id_proposta;
    
    // Buscar id_comentario_pai em múltiplos lugares para garantir que sempre seja encontrado
    const idComentarioPai = dadosExtra?.id_comentario_pai 
        || dadosParaProcessar?.id_comentario_pai 
        || dadosAntes?.id_comentario_pai
        || dadosExtra?.id_comentario_principal
        || dadosParaProcessar?.id_comentario_principal
        || dadosAntes?.id_comentario_principal;

    return {
        campos,
        email,
        idComentario,
        idProposta,
        idComentarioPai
    };
};



